import { useCallback } from "react";
import { PaymentMethod } from "#common/enums";
import {
  useCreatePayeePaymentPrefAchMutation,
  useCreatePayeePaymentPrefWireMutation,
} from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import {
  DetailedAchPaymentPrefObjectType,
  DetailedPaymentPrefUnionObjectType,
  DetailedWirePaymentPrefObjectType,
} from "../../common/types";
import { convertAchPaymentPrefToInput, convertWirePaymentPrefToInput } from "./utils";

export const useCreateWirePaymentPref = () => {
  const [createWirePaymentPrefMutation] = useCreatePayeePaymentPrefWireMutation();

  return useCallback(
    async (
      paymentPrefDetail: DetailedWirePaymentPrefObjectType,
      arenaId: string,
    ): Promise<DetailedPaymentPrefUnionObjectType | null> => {
      const input = convertWirePaymentPrefToInput(paymentPrefDetail, arenaId);

      const result = await createWirePaymentPrefMutation({
        variables: { input },
        context: clientContext(),
      });

      return result.data?.createWirePaymentPref?.paymentPref?.paymentPrefFull || null;
    },
    [createWirePaymentPrefMutation],
  );
};

export const useCreateAchPaymentPref = () => {
  const [createAchPaymentPrefMutation] = useCreatePayeePaymentPrefAchMutation();

  return useCallback(
    async (
      paymentPrefDetail: DetailedAchPaymentPrefObjectType,
      arenaId: string,
    ): Promise<DetailedPaymentPrefUnionObjectType | null> => {
      const input = convertAchPaymentPrefToInput(paymentPrefDetail, arenaId);

      const result = await createAchPaymentPrefMutation({
        variables: { input },
        context: clientContext(),
      });

      return result.data?.createAchPaymentPref?.paymentPref?.paymentPrefFull || null;
    },
    [createAchPaymentPrefMutation],
  );
};

export type CreatePayeePaymentPrefFn = (
  paymentPrefDetail: DetailedPaymentPrefUnionObjectType,
  arenaId: string,
) => Promise<DetailedPaymentPrefUnionObjectType | null>;

export const useCreatePayeePaymentPref = (
  paymentMethod: PaymentMethod | undefined,
): CreatePayeePaymentPrefFn => {
  const createWirePaymentPref = useCreateWirePaymentPref();
  const createAchPaymentPref = useCreateAchPaymentPref();

  return useCallback(
    async (
      paymentPrefDetail: DetailedPaymentPrefUnionObjectType,
      arenaId: string,
    ): Promise<DetailedPaymentPrefUnionObjectType | null> => {
      if (
        paymentMethod === PaymentMethod.Wire &&
        paymentPrefDetail.__typename === "WirePaymentPrefObjectType"
      ) {
        return createWirePaymentPref(paymentPrefDetail, arenaId);
      }

      if (
        paymentMethod === PaymentMethod.ACH &&
        paymentPrefDetail.__typename === "AchPaymentPrefObjectType"
      ) {
        return createAchPaymentPref(paymentPrefDetail, arenaId);
      }

      return null;
    },
    [paymentMethod, createWirePaymentPref, createAchPaymentPref],
  );
};
